import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [permissions, setPermissions] = useState([]);
  const [allowedResources, setAllowedResources] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const clearPermissionData = () => {
    setPermissions([]);
    setAllowedResources([]);
    setIsAdmin(false);
  };

  const loadMyPermissions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/permissions/me`);
      const data = response.data.data || {};
      setPermissions(data.permissions || []);
      setAllowedResources(data.resources || []);
      setIsAdmin(!!data.isAdmin);
    } catch (error) {
      console.error('Permission loading failed:', error);
      clearPermissionData();
    }
  };

  const hasAccess = (resource) => {
    if (isAdmin) return true;
    if (allowedResources.includes('*')) return true;
    return allowedResources.includes(resource);
  };
  // Set up axios defaults whenever token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/verify`);
          setUser(response.data.data.user);
          await loadMyPermissions();
        } catch (error) {
          console.error('Token verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password,
      });

      const { user: userData, token: newToken } = response.data.data;
axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setUser(userData);
      setToken(newToken);
      localStorage.setItem('token', newToken);
await loadMyPermissions();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  };

  // Signup
  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, userData);

      const { user: newUser, token: newToken } = response.data.data;
axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setUser(newUser);
      setToken(newToken);
      localStorage.setItem('token', newToken);
await loadMyPermissions();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Signup failed',
      };
    }
  };

  // Logout — notify server for audit trail, then clear local session
  const logout = async () => {
    try {
      if (token) {
        await axios.post(
          `${API_BASE_URL}/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      // Still clear local session if server call fails
    }
    setUser(null);
    setToken(null);
    clearPermissionData();
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    login,
    signup,
    logout,
    loading,
    isAuthenticated: !!user,
    permissions,
    allowedResources,
    isAdmin,
    hasAccess,
    refreshPermissions: loadMyPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
